import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Check2Square, XLg, ExclamationTriangleFill } from 'react-bootstrap-icons';
import { removeBetFromBetSlip, removeBetBuilderGroup, setBetSlipBanker, setBetSlipEnabled } from 'stores/betSlip';
import { getMatchTime, MarketIdToMarketName, OddsValueTypeTextCreator } from 'toolkit/utils';
import { getMatchDetail, getTeamNameHelper } from 'helper/matchDetailHelper';
import { sportTypeToSportId } from 'toolkit/utils.js';
import { AWS_BUCKET_URL } from 'toolkit/constants';
import { EnumMarketName, EnumEventStatus } from 'toolkit/Enums.js';
import { gameDateToMatchDay, gameDateToMatchTime } from 'toolkit/utils';
import useOddTransformer from 'toolkit/useHooks/useOddsTransformer';
import { checkIfLiveEvent } from 'toolkit/utils';
import { marketDisplayName } from 'views/outrights/utils';
import { selectEventById as selectOutrightEventById } from 'stores/outrights';

export const BetSlipRow = ({ bet, type, isBetBuilderGroup = false, betBuilderGroupBets = [], onRemoveBetBuilderGroup }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('translations', { useSuspense: false });
  const navigate = useNavigate();
  const betSlip = useSelector((state) => state.betSlip);
  const match = useSelector((state) => state.match.events[bet.providerId]);
  const selectedLocale = useSelector((state) => state.match.selectedLocale);
  const locales = useSelector((state) => state.match.locales);
  const [oddsClass, setOddsClass] = useState('');
  const prevOddsRef = useRef(null);

  // Outright bets aren't stored in state.match.events. Prefer live data from
  // the outrights store (so odds updates flow through), then fall back to the
  // bet's embedded snapshot built by OutrightOddButton.buildOutrightMatch.
  const isOutright = !!bet?.match?.isOutright;
  const liveOutrightEvent = useSelector((state) =>
    isOutright ? selectOutrightEventById(state, bet.providerId) : null
  );
  const sourceMatch = isOutright
    ? (liveOutrightEvent
        ? { ...bet.match, markets: liveOutrightEvent.markets || bet.match.markets }
        : bet.match)
    : match;

  const oddsTransformer = useOddTransformer();
  let marketName = '';
  let playerOrTeam = '';

  const { matchInfo, outcomeData } = useMemo(() => {
    if (!sourceMatch) {
      return { matchInfo: null, outcomeData: { odds: bet.odds || 0 } };
    }

    let info;
    if (isOutright) {
      // Build a minimal home/away shape so the existing layout renders.
      info = {
        home: { id: sourceMatch.providerId, name: sourceMatch.eventName, homeScore: 0 },
        away: { id: '', name: '', awayScore: 0 }
      };
    } else {
      info = getMatchDetail(sourceMatch);
    }

    const marketEntry = sourceMatch?.markets?.find((m) => m.stringId === bet.marketStringId);
    const outcome = marketEntry?.outcomes?.find((o) => o.id === bet.outcomeId);

    if (!outcome) {
      // Outrights aren't refreshed via the match feed; keep the stored snapshot.
      if (isOutright) {
        return {
          matchInfo: info,
          outcomeData: { id: bet.outcomeId, name: bet.outcomeName, odds: bet.odds || 0 }
        };
      }
      dispatch(
        removeBetFromBetSlip({
          providerId: bet.providerId,
          marketStringId: bet.marketStringId,
          outcomeId: bet.outcomeId
        })
      );
      return { matchInfo: info, outcomeData: { odds: 0 } };
    }

    return { matchInfo: info, outcomeData: outcome };
  }, [sourceMatch, isOutright, bet.marketStringId, bet.outcomeId, bet.outcomeName, bet.odds, bet.providerId, dispatch]);

  useEffect(() => {
    const newOdd = outcomeData?.odds;
    if (!prevOddsRef.current) {
      prevOddsRef.current = newOdd;
      return;
    }
    const prevOdd = prevOddsRef.current;

    if (newOdd !== prevOdd) {
      setOddsClass(`odd-${newOdd > prevOdd ? 'increased-slip' : 'decreased-slip'}`);
      prevOddsRef.current = newOdd;

      const highlightResetTimer = setTimeout(() => {
        setOddsClass('');
      }, 5000);

      return () => {
        clearTimeout(highlightResetTimer);
      };
    }
  }, [outcomeData?.odds]);

  const removeOdds = useCallback((b) => {
    // Check if it's a bet builder bet - remove entire group
    if (b.isBetBuilder && b.betBuilderId !== undefined) {
      dispatch(removeBetBuilderGroup({ betBuilderId: b.betBuilderId }));
    } else {
      dispatch(
        removeBetFromBetSlip({
          providerId: b.providerId,
          marketStringId: b.marketStringId,
          outcomeId: b.outcomeId
        })
      );
    }
  }, [dispatch]);

  const toggleMatch = useCallback(() => {
    dispatch(setBetSlipEnabled({ providerId: bet.providerId, enabled: !bet.enabled }));
  });

  const toggleBanker = useCallback(() => {
    dispatch(setBetSlipBanker({ providerId: bet.providerId, banker: !bet.banker }));
  });

  const handleRowHeaderClick = useCallback(() => {
    if (type === 'desktop') return;
    navigate(`/match-detail#${bet.providerId}`);
  }, [type, navigate, bet.providerId]);

  {
    if (!sourceMatch || !matchInfo) return <div />;
  }

  // Only the MyOdds price is real; the product of single odds ignores correlation, so never show it
  const betBuilderCalculatedOdds = isBetBuilderGroup && betBuilderGroupBets.length > 0
    ? betBuilderGroupBets[0].betBuilderCalculatedOdds
    : null;
  const isBetBuilderPriced = betBuilderCalculatedOdds > 1;

  const getOutcomeDisplayText = (b, outcome) => {
    if (!match || !outcome) return b.outcomeName || '';
    if (b.marketId === EnumMarketName.ANYTIMEGOALSCORERWINNER_888 || b.marketId === EnumMarketName.NEXTGOALSCORERWINNER_891) {
      let name = outcome.name.split(',')[1] ?? '';
      let surname = outcome.name.split(',')[0] ?? '';
      return (name + ' ' + surname) + ',' + OddsValueTypeTextCreator(match.sport.toString(), b.marketId.toString(), outcome.id, outcome.name);
    }
    if (b.marketId === EnumMarketName.ANYTIMEGOALSCORER_40 || b.marketId === EnumMarketName.NEXTGOALSCORER_38 || b.marketId === EnumMarketName.LASTGOALSCORER_39) {
      return OddsValueTypeTextCreator(match.sport.toString(), b.marketId.toString(), outcome.id, outcome.name) + ',' + (outcome.name.split(',')[1] ?? '') + ' ' + (outcome.name.split(',')[0] ?? '');
    }
    if (b.marketId == 534) {
      return b.outcomeName.includes(',') ? (b.outcomeName.split(',')[1] + ' ' + b.outcomeName.split(',')[0]) : OddsValueTypeTextCreator(match.sport.toString(), b.marketId.toString(), outcome.id, outcome.name);
    }
    return OddsValueTypeTextCreator(match.sport.toString(), b.marketId.toString(), outcome.id, outcome.name);
  };

  if (isBetBuilderGroup) {
    const matchTitle = `${getTeamNameHelper(locales, selectedLocale, matchInfo.home, t)} vs ${getTeamNameHelper(locales, selectedLocale, matchInfo.away, t)}`;
    return (
      <div
        className={`bet-slip-betbuilder-card${isBetBuilderPriced ? '' : ' bet-slip-betbuilder-card-stale'}`}
        style={{ opacity: bet.enabled ? 1 : 0.3 }}>
        <div className="bet-slip-betbuilder-header">
          <span className="bet-slip-betbuilder-title">{matchTitle}</span>
          <div className="d-flex align-items-center">
            <span>{isBetBuilderPriced ? Number(betBuilderCalculatedOdds).toFixed(2) : '-'}</span>
            <button
              type="button"
              className="bet-slip-betbuilder-header-remove"
              onClick={() => onRemoveBetBuilderGroup && onRemoveBetBuilderGroup()}
              aria-label={t('betBuilder.removeCustomBet', 'Remove custom bet')}
              title={t('betBuilder.removeCustomBet', 'Remove custom bet')}>
              <XLg size={14} />
            </button>
          </div>
        </div>
        {!isBetBuilderPriced && (
          <div className="bet-slip-betbuilder-warning" role="alert">
            <ExclamationTriangleFill size={14} className="me-2 flex-shrink-0" />
            <span>{t('betBuilder.recalculate')}</span>
          </div>
        )}
        <div className="bet-slip-betbuilder-body">
          <div className="bet-slip-betbuilder-match-row">
            <Check2Square fill="#28a745" size={20} className="me-2" />
            <span>{getTeamNameHelper(locales, selectedLocale, matchInfo.home, t)} - {getTeamNameHelper(locales, selectedLocale, matchInfo.away, t)}</span>
            <div className="bet-slip-betbuilder-match-date">
              <div>{gameDateToMatchDay(match.gameDate)}</div>
              <div>{gameDateToMatchTime(match.gameDate)}</div>
            </div>
          </div>
          <hr className="bet-slip-betbuilder-separator" />
          {betBuilderGroupBets.map((b, index) => {
            const market = match.markets.find((m) => m.stringId === b.marketStringId);
            let outcome = market?.outcomes.find((o) => o.id === b.outcomeId);
            if (outcome === undefined || outcome === null) outcome = { id: b.outcomeId, name: b.outcomeName, odds: 0 };
            const marketName = b.marketId == 534
              ? (b.outcomeName.includes(',') ? t('whogoalscorer') : t('whochampion'))
              : MarketIdToMarketName(b.marketId, b.marketStringId, false);
            const outcomeText = getOutcomeDisplayText(b, outcome);
            return (
              <React.Fragment key={b.providerId + b.marketStringId + b.outcomeId}>
                <div className="bet-slip-betbuilder-selection-row">
                  <span className="bet-slip-betbuilder-selection-market">{marketName}</span>
                  <span className="bet-slip-betbuilder-selection-outcome">{outcomeText}</span>
                </div>
                {index < betBuilderGroupBets.length - 1 && <hr className="bet-slip-betbuilder-separator" />}
              </React.Fragment>
            );
          })}
          <button
            type="button"
            className="bet-slip-betbuilder-delete-link"
            onClick={() => onRemoveBetBuilderGroup && onRemoveBetBuilderGroup()}
          >
            {t('betBuilder.removeCustomBet', 'Remove custom bet')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`bet-slip-row fs-8`}
      style={{ opacity: bet.enabled ? 1 : 0.3 }}>
      <Row className="px-3 ">
        <div className="col-auto">
          <a onClick={() => toggleMatch()}>
            <Check2Square fill={bet.enabled ? 'green' : 'grey'} size={22}></Check2Square>
          </a>
        </div>
        <div className="col" onClick={handleRowHeaderClick}>
          <div
            className={`d-flex justify-content-between mb-2 ${type === 'desktop' ? '' : 'cursor-pointer'}`}>
            <span>
              {isOutright
                ? sourceMatch.eventName
                : `${getTeamNameHelper(locales, selectedLocale, matchInfo.home, t)} - ${getTeamNameHelper(locales, selectedLocale, matchInfo.away, t)}`}
            </span>
            <div className="col-auto">
              {betSlip.system ? (
                <span>
                  <a
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleBanker();
                    }}>
                    <div
                      className="banker-bg"
                      style={{ backgroundColor: bet.banker ? '#3AA843' : '#E5E5E5' }}>
                      <span className="text-white fw-bold fs-8 m-0 d-flex justify-content-center">
                        B
                      </span>
                    </div>
                  </a>
                </span>
              ) : null}
            </div>
            {isOutright ? null : sourceMatch.eventStatus == EnumEventStatus.RUNNING ? (
              <span>
                {matchInfo.home.homeScore} : {matchInfo.away.awayScore}
              </span>
            ) : (
              <span>
                <div>{gameDateToMatchDay(sourceMatch.gameDate)}</div>
                <div>{gameDateToMatchTime(sourceMatch.gameDate)}</div>
              </span>
            )}
          </div>
          <hr className="mt-1 mb-1" />
          {betSlip.bets.filter((b) => b.providerId === bet.providerId && !b.isBetBuilder).map((b) => {
            if (b.providerId === bet.providerId) {
              const bIsOutright = !!b.match?.isOutright;
              const bSourceMatch = bIsOutright
                ? (liveOutrightEvent
                    ? { ...b.match, markets: liveOutrightEvent.markets || b.match.markets }
                    : b.match)
                : match;
              const sportForOdds = bSourceMatch?.sport != null ? bSourceMatch.sport.toString() : '0';
              const market = bSourceMatch?.markets?.find((m) => m.stringId === b.marketStringId);
              let outcome = market?.outcomes?.find((o) => o.id === b.outcomeId);
              if (outcome === undefined || outcome === null)
                outcome = { id: b.outcomeId, name: b.outcomeName, odds: 0 };
              let playerName;
              let name;
              let surname;
              const outcomeName = outcome.name || '';
              if (outcomeName.includes('&')) {
                playerName = outcomeName.split('&')[0];
                name = playerName.split(',')[1] ?? '';
                surname = playerName.split(',')[0] ?? '';
              } else {
                name = outcomeName.split(',')[1] ?? '';
                surname = outcomeName.split(',')[0] ?? '';
              }
              playerName = name + ' ' + surname;
              if (bIsOutright) {
                // Outright markets translate via outrights.markets.<stringId> with
                // a fallback to the raw market.name from the API.
                const outrightMarket = market || { stringId: b.marketStringId, name: '' };
                marketName = marketDisplayName(outrightMarket, t);
                const rawOutcome = b.outcomeName || outcome.name || '';
                // Player-name markets store "Surname, Name" — reorder to "Name Surname".
                if (rawOutcome.includes(',')) {
                  const parts = rawOutcome.split(',');
                  playerOrTeam = ((parts[1] || '').trim() + ' ' + (parts[0] || '').trim()).trim();
                } else {
                  playerOrTeam = rawOutcome;
                }
              } else if (b.marketId == 534) {
                if (b.outcomeName?.includes(',')) {
                  marketName = t('whogoalscorer');
                  playerOrTeam = b.outcomeName.split(',')[1] + ' ' + b.outcomeName.split(',')[0];
                } else {
                  marketName = t('whochampion');
                  playerOrTeam = OddsValueTypeTextCreator(
                    sportForOdds,
                    b.marketId.toString(),
                    outcome.id,
                    outcome.name
                  );
                }
              } else {
                marketName = MarketIdToMarketName(b.marketId, b.marketStringId, false);
              }
              return (
                <div key={b.providerId + b.marketStringId + outcome.id} className="py-3 px-0">
                  <div className="d-flex justify-content-between">
                    <div className="col-4">
                      <span>{marketName}</span>
                    </div>
                    <div className="col-1"></div>
                    <div className="col-3">
                      <span>
                        {b.marketId === EnumMarketName.ANYTIMEGOALSCORERWINNER_888 ||
                          b.marketId === EnumMarketName.NEXTGOALSCORERWINNER_891
                          ? playerName +
                          ',' +
                          OddsValueTypeTextCreator(
                            sportForOdds,
                            b.marketId.toString(),
                            outcome.id,
                            outcome.name
                          )
                          : b.marketId === EnumMarketName.ANYTIMEGOALSCORER_40 ||
                            b.marketId === EnumMarketName.NEXTGOALSCORER_38 ||
                            b.marketId === EnumMarketName.LASTGOALSCORER_39
                            ? OddsValueTypeTextCreator(
                              sportForOdds,
                              b.marketId.toString(),
                              outcome.id,
                              outcome.name
                            ) +
                            ',' +
                            playerName
                            : (bIsOutright || b.marketId == 534)
                              ? playerOrTeam
                              : OddsValueTypeTextCreator(
                                sportForOdds,
                                b.marketId.toString(),
                                outcome.id,
                                outcome.name
                              )}
                      </span>
                    </div>
                    <div className="col-1"></div>
                    <div className="col-2">
                      {market && (
                        <>
                          {market.marketStatus !== 6 && (
                            <img
                              width="20"
                              height="20"
                              className="light-black-svg"
                              src={`${AWS_BUCKET_URL}/Icons/lockicon.svg`}
                            />
                          )}
                          {market.marketStatus === 6 && (
                            <span className={oddsClass}>
                              {oddsTransformer.transformWithDisplayFormatted(
                                checkIfLiveEvent(b.match),
                                b?.match?.isSeason,
                                b?.marketId,
                                new Date(b?.match?.gameDate),
                                b?.odds,
                                b?.marketStringId
                              )}
                            </span>
                          )}
                          <br />
                          {!bIsOutright && bSourceMatch?.eventStatus == EnumEventStatus.RUNNING &&
                            bSourceMatch?.sport === sportTypeToSportId('Football') &&
                            getMatchTime(bSourceMatch, t)}
                          {!bIsOutright && bSourceMatch?.eventStatus == EnumEventStatus.RUNNING &&
                            bSourceMatch?.sport !== sportTypeToSportId('Football') && (
                              <span>
                                {t(
                                  `components.matchcard.time.byMatchStatusId.${bSourceMatch?.matchStatusId}`
                                )}
                              </span>
                            )}
                        </>
                      )}
                    </div>
                    <div className="col-1">
                      <span>
                        <a onClick={(e) => {
                          e.stopPropagation();
                          removeOdds(b);
                        }}>
                          <img src={`${AWS_BUCKET_URL}/Icons/116.png`}></img>
                        </a>
                      </span>
                    </div>
                  </div>
                </div>
              );
            }
          })}
        </div>
      </Row>
    </div>
  );
};
